<template>
  <div
      class="autocomplete-popup__input-container q-input-group q-mb-1 q-input-group q-flex-nowrap"
      :class="containerClassList"
  >
    <!-- WRAPPER dla inputa i clear buttona -->
    <div class="autocomplete-search__input-wrap" :class="{'has-action': showButtonOnInput}">
      <input
          ref="inputElement"
          v-model="searchQuery"
          @keyup="handleKeyUpConditional"
          @keyup.enter="handleRedirection"
          @focus="popupStore.showAutocomplete"
          type="text"
          :class="inputClassList"
          :style="inputInlineStyle"
          id="autocomplete-search__input"
          :placeholder="SmartSearchTranslations.autocomplete.whatSearch"
          :aria-label="SmartSearchTranslations.autocomplete.typeTerm"
          aria-describedby="search-input"
          autocomplete="off"
      >
      <!-- Clear Query Button - zawsze względem inputa -->
      <ClearQueryButton
          class="clear-query-affix"
          :style="{ right: clearRight + 'px' }"
          :autocomplete="autocomplete"
      />
    </div>

    <!-- przycisk akcji -->
    <button
        v-if="showButtonOnInput"
        class="autocomplete-search__input-button q-btn q-btn-danger q-shadow-sm"
        type="button"
        id="autocomplete-search__input-button"
        @click="handleRedirection"
    >
      {{ SmartSearchTranslations.autocomplete.inputButton }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, inject, PropType, ref, toRefs } from 'vue';
import { useSearchQueryStore } from "@/store/SearchQueryStore";
import { usePopupStore } from "@/store/AutocompletePopup/PopupStore";
import { useUrlStore } from "@/store/SerpPage/UrlStore";
import ClearQueryButton from "@/components/SerpPage/ResultsContainer/ClearQueryButton.vue";
import { SmartSearchConfigInterface, SmartSearchTranslationsInterface } from "@/core/types";
import DOMPurify from "dompurify";

const props = defineProps({
  autocomplete: Boolean,
  context: {
    type: String as PropType<'desktop' | 'mobile'>,
    default: 'desktop'
  }
});

const urlStore = useUrlStore();
const queryStore = useSearchQueryStore();
const { searchQuery } = toRefs(queryStore);
const popupStore = usePopupStore();

const inputElement = ref<HTMLInputElement | null>(null);

const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const SmartSearchTranslations = ref(inject<SmartSearchTranslationsInterface>('SmartSearchTranslations')!);

const { redirectionUrl } = SmartSearchConfig.value.appConfiguration;

const isMobileContext = computed(() => props.context === 'mobile');

const showButtonSetting = computed(() => props.autocomplete
    ? SmartSearchConfig.value.autocomplete.showButtonOnInput
    : SmartSearchConfig.value.serp.showButtonOnInput);

const showButtonOnInput = computed(() => showButtonSetting.value);

const inputInlineStyle = computed(() => isMobileContext.value ? {} : {
  position: 'relative',
  zIndex: 1000000
});

const inputClassList = computed(() => [
  'autocomplete-search__input',
  'q-form-control',
  'q-position-relative',
  'q-rounded-3',
  'q-shadow-sm',
  `autocomplete-search__input--${isMobileContext.value ? 'mobile' : 'desktop'}`,
  {
    'q-input-with-button': showButtonOnInput.value,
    'autocomplete-search__input-max-width': props.autocomplete && !isMobileContext.value
  }
]);

const containerClassList = computed(() => ({
  'autocomplete-popup__input-container--mobile': isMobileContext.value,
  'autocomplete-popup__input-container--desktop': !isMobileContext.value
}));

const customRedirects = window?._QONizer?.customSmartSearchConfig?.customRedirects || [];

const matchedRedirect = computed(() => {
  const queryLower = searchQuery.value.toLowerCase().trim();
  return customRedirects.find(redirect =>
      redirect.phrases.some(phrase => phrase.toLowerCase().trim() === queryLower)
  );
});

const handleKeyUpConditional = () => {
  if (props.autocomplete) {
    popupStore.handleKeyUp();
  }
};

const handleInputPointerDown = () => {
  if (!props.autocomplete) return;
  popupStore.showAutocomplete();
};

const handleRedirection = () => {
  const q = (queryStore.searchQuery || '').trim();

  if (!q) {
    popupStore.showAutocomplete();
    inputElement.value?.focus();
    return;
  }

  if (matchedRedirect.value) {
    window.location.href = matchedRedirect.value.url;
  } else {
    const sanitised = DOMPurify.sanitize(q);
    urlStore.goToSerpPage(
        redirectionUrl,
        sanitised
    );
  }
};

// dynamiczna pozycja Clear w zależności od obecności przycisku akcji
const clearRight = computed(() => (showButtonOnInput.value ? 56 : 12));

// eslint-disable-next-line no-undef
defineExpose({
  focusInput: () => inputElement.value?.focus()
});
</script>

<style>
.autocomplete-popup__input-container--mobile {
  width: 100%;
}

.autocomplete-search__input-max-width {
  max-width: 600px !important;
  margin-left: 0px;
}

.autocomplete-popup__input-container::before {
  content: "";
  display: block;
  position: absolute;
  background-color: rgb(149, 150, 153);
  -webkit-mask-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHRpdGxlPnNlYXJjaDwvdGl0bGU+PHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBzdHlsZT0iZmlsbDojZmZmO29wYWNpdHk6MCIvPjxwYXRoIGQ9Ik0yMS44MSwyMC42NmwtNC41Mi00LjUxYTguNjMsOC42MywwLDEsMC0xLjA2LDEuMDZsNC41MSw0LjUxWk0zLjczLDEwLjczYTcsNywwLDEsMSw3LDdBNyw3LDAsMCwxLDMuNzMsMTAuNzNaIi8+PC9zdmc+");
  -webkit-mask-size: 20px 20px;
  -webkit-mask-repeat: no-repeat;
  width: 20px;
  height: 20px;
  top: 50%;
  left: 10px;
  transform: translate(0px, -50%);
  z-index: 1040;
}

.autocomplete-search__input {
  position: relative;
  padding-left: 35px !important;
  border: 1px solid #fff;
  padding-right: 40px !important; /* miejsce na Clear */
}

.autocomplete-search__input:focus {
  border: 1px solid var(--q-danger);
}

/* Wrapper, by Clear był względem inputa */
.autocomplete-search__input-wrap {
  position: relative;
  display: inline-block;
  width: 100%;
}

/* Clear zawsze względem inputa */
.clear-query-affix {
  position: absolute !important;
  top: 50%;
  transform: translateY(-50%);
  right: 12px;
  z-index: 1;
}

/* gdy jest przycisk akcji — więcej paddingu */
.autocomplete-search__input-wrap.has-action .autocomplete-search__input {
  padding-right: 96px !important;
}

/* gdy przycisk akcji jest obecny — Clear przesunięty bardziej w lewo */
.autocomplete-search__input-wrap.has-action .clear-query-affix {
  right: 10px !important;
}

.q-input-with-button {
  border-bottom-right-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

/* === MOBILE ONLY === */
:where(body, html):not(.q-autocomplete-active)
#smartsearch-autocomplete[data-qonizer-autocomplete-body-placement="true"]
.autocomplete-popup__input-container-group {
  display: none !important;
}

#smartsearch-autocomplete[data-qonizer-autocomplete-body-placement="true"]
.autocomplete-popup__input-container--mobile {
  display: none;
}

.q-autocomplete-active
#smartsearch-autocomplete[data-qonizer-autocomplete-body-placement="true"]
.autocomplete-popup__input-container--mobile {
  display: block !important;
  position: relative;
}

/* Gmail-like bar: brak ramki, jasne tło, duży padding */
@media (max-width: 991.98px) {
  .q-autocomplete-active
  .autocomplete-popup__input-container--mobile
  .autocomplete-search__input--mobile {
    -webkit-appearance: none;
    appearance: none;
    width: 100% !important;
    height: 44px !important;
    background: #ffffff !important;
    color: #111827 !important;
    border: none !important;
    border-radius: 12px !important;
    padding: 10px 14px 10px 35px !important;
    padding-right: 40px !important; /* miejsce na Clear */
    line-height: 1.25 !important;
    outline: none !important;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .06);
    transition: box-shadow .18s ease, background .18s ease;
  }

  .q-autocomplete-active
  .autocomplete-popup__input-container--mobile
  .autocomplete-search__input-wrap.has-action
  .autocomplete-search__input--mobile {
    padding-right: 96px !important;
  }

  .q-autocomplete-active
  .autocomplete-popup__input-container--mobile
  .autocomplete-search__input--mobile:focus {
    background: #fff;
    box-shadow:
        inset 0 0 0 1px rgba(0, 0, 0, .06),
        0 0 0 2px rgba(0, 0, 0, .07);
  }

  .q-autocomplete-active
  .autocomplete-popup__input-container--mobile
  .autocomplete-search__input--mobile::placeholder {
    color: #9ca3af;
  }

  .q-autocomplete-active
  .autocomplete-popup__input-container--mobile
  .autocomplete-search__input-button {
    display: none !important;
  }

  .autocomplete-search__input--mobile,
  .autocomplete-search__input--mobile:focus {
    border-color: transparent !important;
  }
}
</style>
